import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { getCatalogProducts, type ProductListItem, type ProductListQuery } from "@/lib/api-client";
import { ProductCard } from "./ProductCard";

export interface ProductGridProps {
  initialItems: ProductListItem[];
  initialNextCursor: string | null;
  /** Filtres/tri courants (sans `cursor`) — réutilisés tels quels pour charger la page suivante. */
  query: ProductListQuery;
  emptyMessage?: string;
}

/**
 * `GrilleProduits` : 2 colonnes (mobile) → 3 (tablette) → 4 (bureau) → 5
 * (grand écran), cf. VVS_MODELE_RESPONSIVE.md §6.4. Pagination « Charger plus »
 * par curseur (§3.2) : les éléments déjà chargés restent affichés, pas de
 * rechargement de page.
 *
 * Porté depuis apps/web/components/product/ProductGrid.tsx : la Server
 * Action `loadMoreProducts` (./actions.ts) est remplacée par un appel direct
 * à `getCatalogProducts()` (déjà un simple `fetch()` navigateur ici, plus
 * besoin d'un point d'entrée serveur dédié) ; `useTransition` remplacé par un
 * booléen `isPending` local, équivalent pour cet usage.
 *
 * IMPORTANT : le composant est monté avec `key={JSON.stringify(query)}` par
 * `ListingControls` — `items`/`nextCursor` ne sont initialisés qu'au montage
 * (`useState(initialItems)`), donc un changement de filtres/tri (qui ne
 * change QUE les props sans démonter le composant) ne les rafraîchirait pas
 * sans cette clé forçant un remontage. Dans apps/web (Server Component), ce
 * n'était pas un problème : chaque navigation était un rendu serveur complet.
 */
export function ProductGrid({ initialItems, initialNextCursor, query, emptyMessage }: ProductGridProps) {
  const [items, setItems] = useState(initialItems);
  const [nextCursor, setNextCursor] = useState(initialNextCursor);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleLoadMore() {
    if (!nextCursor) return;
    setError(null);
    setIsPending(true);
    try {
      const response = await getCatalogProducts({ ...query, cursor: nextCursor });
      setItems((prev) => [...prev, ...response.items]);
      setNextCursor(response.nextCursor);
    } catch {
      setError("Impossible de charger plus de produits pour le moment. Réessayez.");
    } finally {
      setIsPending(false);
    }
  }

  if (items.length === 0) {
    return (
      <p className="py-16 text-center font-body text-sm text-muted">
        {emptyMessage ?? "Aucun produit ne correspond à votre sélection pour le moment."}
      </p>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5">
        {items.map((item) => (
          <ProductCard key={item.id} product={item} />
        ))}
      </div>

      {nextCursor ? (
        <div className="mt-8 flex flex-col items-center gap-2">
          <Button variant="secondary" onClick={handleLoadMore} disabled={isPending}>
            {isPending ? "Chargement…" : "Charger plus"}
          </Button>
          {error ? <p className="font-body text-xs text-red-600">{error}</p> : null}
        </div>
      ) : null}
    </div>
  );
}
